/**
 * trigger validate
 */

import type {
    ClickTriggerConfigure,
    CreateRecordTriggerConfigure,
    EmptyTriggerConfigure,
    MatchConditionTriggerConfigure,
    ScheduledTriggerConfigure,
    UpdateRecordTriggerConfigure,
    WebhookTriggerConfigure,
    WebsocketTriggerConfigure
} from '@lighthouse/shared'
import { TriggerNodeType } from '@lighthouse/shared'

import type { NodeValidator, TriggerValidator } from '../types'

const emptyTriggerValidator: NodeValidator<TriggerValidator<EmptyTriggerConfigure>, TriggerNodeType.EMPTY_TRIGGER> = {
    nodeType: TriggerNodeType.EMPTY_TRIGGER,
    validators: [
        node => {
            return !!node.data.nodeType && node.data.nodeType !== TriggerNodeType.EMPTY_TRIGGER
        }
    ]
}

// create record trigger
const createRecordTriggerValidator: NodeValidator<TriggerValidator<CreateRecordTriggerConfigure>, TriggerNodeType.CREATE_RECORD_TRIGGER> = {
    nodeType: TriggerNodeType.CREATE_RECORD_TRIGGER,
    validators: [
        node => {
            return !!node.data.config?.dataSourceId
        }
    ]
}
// update record trigger
const updateRecordTriggerValidator: NodeValidator<TriggerValidator<UpdateRecordTriggerConfigure>, TriggerNodeType.UPDATE_RECORD_TRIGGER> = {
    nodeType: TriggerNodeType.UPDATE_RECORD_TRIGGER,
    validators: [
        // dsId required
        node => {
            return !!node.data.config?.dataSourceId
        },
        // 监听字段变更
        node => {
            return !!node.data.config?.watchedFields?.length
        }
    ]
}
// match record trigger
const matchRecordTriggerValidator: NodeValidator<
    TriggerValidator<MatchConditionTriggerConfigure>,
    TriggerNodeType.MATCH_CONDITION_TRIGGER
> = {
    nodeType: TriggerNodeType.MATCH_CONDITION_TRIGGER,
    validators: [
        // dsId required
        node => {
            return !!node.data.config?.dataSourceId
        }
    ]
}
// schedule trigger
const scheduleTriggerValidator: NodeValidator<TriggerValidator<ScheduledTriggerConfigure>, TriggerNodeType.SCHEDULED_TRIGGER> = {
    nodeType: TriggerNodeType.SCHEDULED_TRIGGER,
    validators: [
        // cron required
        node => {
            return !!node.data.config?.scheduleStartTime
        },
        node => {
            switch (node.data.config?.scheduleRule) {
                case 'MINUTE': {
                    return !!node.data.config?.scheduleMinuteRule
                }

                case 'HOUR': {
                    return !!node.data.config?.scheduleHourRule
                }

                case 'DAY': {
                    return !!node.data.config?.scheduleDayRule?.day && !!node.data.config.scheduleDayRule?.time
                }

                case 'WEEKDAY': {
                    return !!node.data.config?.scheduleWeekdayRule
                }

                case 'WEEK': {
                    return !!node.data.config?.scheduleWeekRule?.days
                }
                case 'MONTH': {
                    return !!node.data.config?.scheduleMonthRule?.days
                }

                default: {
                    return !!node.data.config?.onceTime
                }
            }
        }
    ]
}

// click trigger
const clickTriggerValidator: NodeValidator<TriggerValidator<ClickTriggerConfigure>, TriggerNodeType.CLICK_TRIGGER> = {
    nodeType: TriggerNodeType.CLICK_TRIGGER,
    validators: [
        // clickType required
        node => {
            return true
        }
    ]
}

const webhookTriggerValidator: NodeValidator<TriggerValidator<WebhookTriggerConfigure>, TriggerNodeType.WEBHOOK_TRIGGER> = {
    nodeType: TriggerNodeType.WEBHOOK_TRIGGER,
    validators: [
        node => {
            const url = node.data.config?.url
            return !!url && !url.includes('{apiKey}')
        }
    ]
}

const websocketTriggerValidator: NodeValidator<TriggerValidator<WebsocketTriggerConfigure>, TriggerNodeType.WEBSOCKET_TRIGGER> = {
    nodeType: TriggerNodeType.WEBSOCKET_TRIGGER,
    validators: [
        // clickType required
        node => {
            return !!node.data.config.url && !!node.data.config.eventName && !!node.data.config.eventValue
        }
    ]
}

export const triggerValidators = [
    emptyTriggerValidator,
    createRecordTriggerValidator,
    updateRecordTriggerValidator,
    matchRecordTriggerValidator,
    scheduleTriggerValidator,
    clickTriggerValidator,
    webhookTriggerValidator,
    websocketTriggerValidator
    // variableTriggerValidator
]
